package com.exura.features.firebase.data.repository

import com.exura.features.firebase.data.mappers.SerializableMappers
import com.exura.features.firebase.data.remote.dto.SerializableDto
import com.exura.features.firebase.domain.model.SerializableDomainModel
import com.exura.features.firebase.domain.repository.SerializableDomainModelRepository
import com.exura.features.plandefinition.data.serialization.seriesSerializersModule
import dev.gitlive.firebase.firestore.CollectionReference
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.DocumentSnapshot
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.modules.SerializersModule

/**
 * Base implementation of FirebaseRepository that provides generic CRUD operations
 * for Firestore collections.
 *
 * @param T the domain model type that extends SerializableDomainModel
 * @param D the DTO type that extends SerializableDto
 * @param firestore the Firestore instance
 * @param mapper the mapper for converting between domain and DTO
 * @param dtoSerializersModule the serializers module for Kotlinx serialization
 */
abstract class BaseFirestoreRepository<T : SerializableDomainModel, D : SerializableDto>(
    protected val firestore: FirebaseFirestore,
    protected val mapper: SerializableMappers<T, D>,
    protected val dtoSerializersModule: SerializersModule,
) : SerializableDomainModelRepository<T> {

    /*
     * in theory
     * doc.data<D> { serializersModule = dtoSerializersModule }
     */
    abstract suspend fun getDto(doc: DocumentSnapshot): D

    /*
    * in theory, something like this
    * dest.add<D>(dto) { serializersModule = dtoSerializersModule }
    * */
    abstract suspend fun addDto(dto: D, dest: CollectionReference): DocumentReference


    /*
    * in theory
    * dest.set<D>{dto) { serializersModule = dtoSerializersModule }
    * dest
    * */
    abstract suspend fun setDto(dto: D, dest: DocumentReference): DocumentReference

    override suspend fun create(item: T, dest: CollectionReference): Result<T> = runCatching {
        // CLAUDE IGNORE: should probably be a better way, not to do double remapping. Something like update?
        val dto = mapper.toDto(item)
        val docRef = addDto(dto, dest)
        mapper.toDomain(dto, dest, docRef.id)
    }

    /**
     * Reads a document from Firestore by its reference
     *
     * @param source The document reference to read
     * @return Result containing the domain model or an error if not found
     */
    override suspend fun read(source: DocumentReference): Result<T> = runCatching {
        val doc = source.get()
        if (!doc.exists) {
            throw IllegalArgumentException("Document at ${source.path} does not exist")
        }

        val dto = getDto(doc)
        mapper.toDomain(dto, source.parent, source.id)
    }

    /**
     * Updates an existing document in Firestore
     *
     * @param item The domain model to update
     * @param dest The document reference where the update should occur
     * @return Result containing the updated domain model or an error
     */
    override suspend fun update(item: T, dest: DocumentReference): Result<T> = runCatching {
        // Check if the document exists
        val existingDoc = dest.get()
        if (!existingDoc.exists) {
            throw IllegalArgumentException("Document at ${dest.path} does not exist. Use create instead.")
        }

        return set(item, dest)
    }


    override suspend fun set(item: T, dest: DocumentReference): Result<T> = runCatching {
        val dto = mapper.toDto(item)

        val docRef = setDto(dto, dest)

        mapper.toDomain(dto, docRef.parent, docRef.id)
    }

    /**
     * Deletes a document from Firestore
     *
     * @param dest The document reference to delete
     * @return Result containing true if deletion was successful, or an error
     */
    override suspend fun delete(dest: DocumentReference): Result<Boolean> = runCatching {
        // Check if document exists
        val existingDoc = dest.get()
        if (!existingDoc.exists) {
            // Document doesn't exist - consider this a successful operation
            // since the end state is what was requested
            return@runCatching true
        }

        // Delete the document
        dest.delete()

        // Verify deletion was successful
        val checkDoc = dest.get()
        !checkDoc.exists
    }

    /**
     * Lists all documents from a collection
     *
     * @param dest The collection reference to list documents from
     * @return Flow of domain model lists that updates when the collection changes
     */
    override suspend fun list(dest: CollectionReference): Flow<List<T>> {
        return dest.snapshots.map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                try {
                    val docRef = doc.reference
                    val dto = getDto(doc)
                    mapper.toDomain(dto, docRef.parent, docRef.id)
                } catch (e: Exception) {
                    // Log error or handle appropriately
                    null
                }
            }
        }
    }
}
